// src/components/headerone/bots/GenesysBot.js
// Genesys bot component - receives userData from Cognito and initializes Genesys bot
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const announceMessengerState = (isOpen) => {
  try {
    window.dispatchEvent(new CustomEvent('gts:botWidgetState', { detail: { isOpen, source: 'genesys' } }));
  } catch {}
};

const cleanupGenesysResources = () => {
  if (typeof window === 'undefined') return;
  const w = window;

  if (typeof w.Genesys === 'function') {
    try {
      const result = w.Genesys('command', 'Messenger.close');
      if (result && typeof result.catch === 'function') {
        result.catch(() => {});
      }
    } catch {}
  }

  if (document.body) {
    document.body.classList.remove('genesys-messenger-open');
    document.body.classList.remove('genesys-messenger-expanded');
    document.body.removeAttribute('data-genesys-messenger-state');
  }

  announceMessengerState(false);
};

function JourneyTracking() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const w = window;
    const scriptUrl = 'https://apps.mypurecloud.com/journey/sdk/js/web/v1/ac.js';
    const selector = `script[src="${scriptUrl}"]`;
    let scriptElement = document.querySelector(selector);

    w._genesysJourneySdk = 'ac';

    if (!scriptElement) {
      scriptElement = document.createElement('script');
      scriptElement.id = 'genesys-journey-sdk';
      scriptElement.async = true;
      scriptElement.src = scriptUrl;
      scriptElement.charset = 'utf-8';
      document.head.appendChild(scriptElement);
    }

    if (typeof w.ac !== 'function') {
      const queue = [];
      const stub = function () {
        queue.push(arguments);
      };
      stub.q = queue;
      stub.l = Date.now();
      w.ac = stub;
    }

    w.__acTrackingSetup = true;
    w.ac('init', '3e983c55-af57-4564-97a2-0bb9d813d184', { region: 'use1' });
    w.ac('load', 'autotrackUrlChange');

    return () => {
      w.__acTrackingSetup = false;
      delete w._genesysJourneySdk;
      delete w.ac;
    };
  }, []);

  return null;
}

function GenesysBootstrap() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const w = window;
    const scriptUrl = 'https://apps.mypurecloud.com/genesys-bootstrap/genesys.min.js';
    const selector = `script[src="${scriptUrl}"]`;
    let scriptElement = document.querySelector(selector);

    if (!w.Genesys) {
      w._genesysJs = 'Genesys';
      w.Genesys = function () {
        (w.Genesys.q = w.Genesys.q || []).push(arguments);
      };
      w.Genesys.t = Date.now();
      w.Genesys.c = {
        environment: 'prod',
        deploymentId: 'c5529a78-f35a-4ab0-bb88-62e6cb5a8e24',
      };
    }

    if (!scriptElement) {
      scriptElement = document.createElement('script');
      scriptElement.id = 'genesys-bootstrap';
      scriptElement.async = true;
      scriptElement.src = scriptUrl;
      scriptElement.charset = 'utf-8';
      document.head.appendChild(scriptElement);
    }

    return () => {
      cleanupGenesysResources();
    };
  }, []);

  return null;
}

function MessengerVisibilityGuard() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window;

    const unhide = () => {
      const root = document.getElementById('genesys-messenger');
      if (root) {
        root.style.removeProperty('display');
        root.style.removeProperty('opacity');
        root.style.removeProperty('visibility');
      }
      const btn = document.getElementById('genesys-messenger-button');
      if (btn) btn.removeAttribute('aria-expanded');
    };

    unhide();

    if (typeof w.Genesys === 'function') {
      try { w.Genesys('subscribe', 'Messenger.ready', unhide); } catch {}
    }

    return () => {
      if (typeof w.Genesys === 'function') {
        try { w.Genesys('unsubscribe', 'Messenger.ready', unhide); } catch {}
      }
    };
  }, []);

  return null;
}

function MessengerAttributes({ customerID, customerName, smsOptIn }) {
  const location = useLocation();

  useEffect(() => {
    const w = window;
    announceMessengerState(false);

    const getBrowserDetails = () => {
      const ua = navigator.userAgent || '';
      const mCriOS = ua.match(/CriOS\/([\d.]+)/); if (mCriOS) return `Chrome iOS ${mCriOS[1]}`;
      const mFxiOS = ua.match(/FxiOS\/([\d.]+)/); if (mFxiOS) return `Firefox iOS ${mFxiOS[1]}`;
      const mEdgiOS = ua.match(/EdgiOS\/([\d.]+)/); if (mEdgiOS) return `Edge iOS ${mEdgiOS[1]}`;
      const mOPiOS = ua.match(/OPiOS\/([\d.]+)/); if (mOPiOS) return `Opera iOS ${mOPiOS[1]}`;
      const mEdge = ua.match(/Edg\/([\d.]+)/);     if (mEdge)  return `Edge ${mEdge[1]}`;
      const mOPR = ua.match(/OPR\/([\d.]+)/);     if (mOPR)   return `Opera ${mOPR[1]}`;
      const mFirefox = ua.match(/Firefox\/([\d.]+)/); if (mFirefox) return `Firefox ${mFirefox[1]}`;
      const mChrome = /Chrome\/([\d.]+)/.exec(ua); if (mChrome && !/Chromium|OPR|Edg/.test(ua)) return `Chrome ${mChrome[1]}`;
      const mSafari = /Safari\/[\d.]+/.test(ua) && ua.match(/Version\/([\d.]+)/); if (mSafari) return `Safari ${mSafari[1]}`;
      const mChromium = ua.match(/Chromium\/([\d.]+)/); if (mChromium) return `Chromium ${mChromium[1]}`;
      const brand = navigator.userAgentData?.brands?.find(b => b.brand && b.brand !== 'Chromium')
        || navigator.userAgentData?.brands?.[0];
      if (brand) return `${brand.brand} ${brand.version}`;
      return 'Unknown';
    };

    const getOS = () => {
      const ua = navigator.userAgent || '';
      if (/Windows NT/i.test(ua)) return 'Windows';
      if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS';
      if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
      if (/Android/i.test(ua)) return 'Android';
      if (/CrOS/i.test(ua)) return 'Chrome OS';
      if (/Linux/i.test(ua)) return 'Linux';
      return navigator.platform || 'Unknown';
    };

    const getScreenResolution = () => `${window.screen?.width || 0}x${window.screen?.height || 0}`;

    const pushAttrs = () => {
      if (!w.Genesys) return;
      const customAttrs = {
        subjectLine: document.title,
        currentURL: window.location.href,
        browserDetails: getBrowserDetails(),
        screenResolution: getScreenResolution(),
        platform: getOS(),
        customerID,        // already last-4 from Cognito
        customerName,
      };
      // Include SMS opt-in if available
      if (smsOptIn) {
        customAttrs['sms_optin'] = smsOptIn;
      }
      w.Genesys(
        'command',
        'Database.update',
        {
          messaging: {
            customAttributes: customAttrs,
          },
        },
        () => {},
        (e) => console.warn('Database.update failed', e)
      );
    };

    const onDbReady = () => pushAttrs();
    const onMsgerReady = () => pushAttrs();
    const onConvConnected = () => pushAttrs();
    const onConvoStarted = () => pushAttrs();
    const onConvoRestored = () => pushAttrs();
    const onConvoClosed = () => pushAttrs();
    const onConvDisconnected = () => pushAttrs();
    const onMessengerOpened = () => announceMessengerState(true);
    const onMessengerMaximized = () => announceMessengerState(true);
    const onMessengerClosed = () => announceMessengerState(false);
    const onMessengerMinimized = () => announceMessengerState(false);

    if (w.Genesys) {
      w.Genesys('subscribe', 'Database.ready', onDbReady);
      w.Genesys('subscribe', 'Messenger.ready', onMsgerReady);
      w.Genesys('subscribe', 'MessagingService.conversationConnected', onConvConnected);
      w.Genesys('subscribe', 'MessagingService.conversationDisconnected', onConvDisconnected);
      w.Genesys('subscribe', 'Conversations.started', onConvoStarted);
      w.Genesys('subscribe', 'Conversations.restored', onConvoRestored);
      w.Genesys('subscribe', 'Conversations.closed', onConvoClosed);
      w.Genesys('subscribe', 'Messenger.opened', onMessengerOpened);
      w.Genesys('subscribe', 'Messenger.maximized', onMessengerMaximized);
      w.Genesys('subscribe', 'Messenger.closed', onMessengerClosed);
      w.Genesys('subscribe', 'Messenger.minimized', onMessengerMinimized);
    }

    pushAttrs();

    return () => {
      if (w.Genesys) {
        w.Genesys('unsubscribe', 'Database.ready', onDbReady);
        w.Genesys('unsubscribe', 'Messenger.ready', onMsgerReady);
        w.Genesys('unsubscribe', 'MessagingService.conversationConnected', onConvConnected);
        w.Genesys('unsubscribe', 'MessagingService.conversationDisconnected', onConvDisconnected);
        w.Genesys('unsubscribe', 'Conversations.started', onConvoStarted);
        w.Genesys('unsubscribe', 'Conversations.restored', onConvoRestored);
        w.Genesys('unsubscribe', 'Conversations.closed', onConvoClosed);
        w.Genesys('unsubscribe', 'Messenger.opened', onMessengerOpened);
        w.Genesys('unsubscribe', 'Messenger.maximized', onMessengerMaximized);
        w.Genesys('unsubscribe', 'Messenger.closed', onMessengerClosed);
        w.Genesys('unsubscribe', 'Messenger.minimized', onMessengerMinimized);
      }
      announceMessengerState(false);
    };
  }, [location.pathname, location.search, customerID, customerName, smsOptIn]);

  return null;
}

function GenesysBot({ userData, payload }) {
  // Map Cognito userData to Genesys format
  // Genesys expects: customerID (last 4), customerName
  const customerID = userData?.customerID || '';
  const customerName = userData?.fullName || '';
  // Get SMS opt-in from payload
  const smsOptIn = payload?.['custom:sms_optin'] || '';

  return (
    <>
      <GenesysBootstrap />
      <JourneyTracking />
      <MessengerVisibilityGuard />
      <MessengerAttributes customerID={customerID} customerName={customerName} smsOptIn={smsOptIn} />
    </>
  );
}

export default GenesysBot;


